import { useCallback, useEffect, useState } from "react";

// Custom event name for syncing busy messages across components
const BUSY_CHANGE_EVENT = "appBusyChange";

// Global state for busy messages (not React state, so no re-renders)
let globalMessages: string[] = [];

// Dispatch a custom event to notify listening components of busy message changes
const dispatchBusyChange = (messages: string[]) => {
  globalMessages = messages;
  window.dispatchEvent(
    new CustomEvent(BUSY_CHANGE_EVENT, {
      detail: { messages },
    })
  );
};

interface UseAppBusyReturn {
  addBusy: (message: string) => void;
  clearBusy: (message?: string) => void;
}

interface UseAppBusyMessagesReturn {
  messages: string[];
}

/**
 * A hook for managing application busy state (adding/clearing messages).
 * This hook does NOT cause re-renders - it only provides functions to modify state.
 *
 * Use this hook in components that need to add or clear busy messages.
 *
 * @returns An object with addBusy and clearBusy functions
 *
 * @example
 * const { addBusy, clearBusy } = useAppBusy();
 *
 * // Add a busy message
 * addBusy("Loading data...");
 * addBusy("Processing...");
 *
 * // Clear a specific message
 * clearBusy("Loading data...");
 *
 * // Clear all messages
 * clearBusy();
 */
export const useAppBusy = (): UseAppBusyReturn => {
  // Add a busy message
  const addBusy = useCallback((message: string): void => {
    // Avoid duplicates
    if (globalMessages.includes(message)) {
      return;
    }
    const newMessages = [...globalMessages, message];
    dispatchBusyChange(newMessages);
  }, []);

  // Clear a specific message or all messages
  const clearBusy = useCallback((message?: string): void => {
    let newMessages: string[];

    if (message === undefined) {
      // Clear all messages
      newMessages = [];
    } else {
      // Remove specific message
      newMessages = globalMessages.filter((msg) => msg !== message);
    }

    dispatchBusyChange(newMessages);
  }, []);

  return { addBusy, clearBusy };
};

/**
 * A hook for reading application busy messages.
 * This hook DOES cause re-renders when messages change.
 *
 * Use this hook ONLY in the UI component that displays busy indicators.
 *
 * @returns An object with the messages array
 *
 * @example
 * const { messages } = useAppBusyMessages();
 *
 * // Check if app is busy
 * const isBusy = messages.length > 0;
 *
 * // Display all busy messages
 * {messages.map((msg, i) => <div key={i}>{msg}</div>)}
 */
export const useAppBusyMessages = (): UseAppBusyMessagesReturn => {
  const [messages, setMessages] = useState<string[]>(globalMessages);

  // Listen for busy message changes from other components
  useEffect(() => {
    const handleBusyChange = (event: Event) => {
      const customEvent = event as CustomEvent<{
        messages: string[];
      }>;

      setMessages(customEvent.detail.messages);
    };

    window.addEventListener(BUSY_CHANGE_EVENT, handleBusyChange);

    return () => {
      window.removeEventListener(BUSY_CHANGE_EVENT, handleBusyChange);
    };
  }, []);

  return { messages };
};
